import { useMemo, useRef } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { SEO } from '@platform/seo';

import { HTTP_STATUS } from '@/shared/constants/app/http.constant.ts';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { buildSeo } from '@/shared/seo/seoBuilder';

import { useDocArticle, useDocsTree } from '../api/use-docs.query';
import { DocsBodyRenderer } from '../components/body/DocsBodyRenderer';
import { DocsFeedback } from '../components/DocsFeedback';
import { DocsPager } from '../components/DocsPager';
import { DocsToc } from '../components/DocsToc';
import { useTocHeadings } from '../hooks/useTocHeadings';
import {
  SECTION_INDEX_SLUG,
  docPath,
  resolveRedirect,
} from '../lib/docsNavigation';

/** `2026-08-27T…` 에서 날짜 부분만. */
const ISO_DATE_LENGTH = 10;

/** 본문 자리를 미리 잡는다. 도착하는 순간 스크롤이 튀면 읽던 위치를 잃는다. */
function ArticleSkeleton() {
  return (
    <div className="my-10 space-y-3" aria-hidden>
      <div className="h-4 w-2/3 rounded bg-neutral-100 dark:bg-white/8" />
      <div className="h-4 w-full rounded bg-neutral-100 dark:bg-white/8" />
      <div className="h-4 w-5/6 rounded bg-neutral-100 dark:bg-white/8" />
      <div className="h-40 w-full rounded-xl bg-neutral-100 dark:bg-white/8" />
    </div>
  );
}

interface DocsArticleViewProps {
  /** URL 파라미터 대신 직접 지정. `/docs/<section>` 이 대표 문서를 그릴 때 쓴다. */
  sectionSlug?: string;
  docSlug?: string;
}

/** 문서 본문 한 건. 사이드바·헤더는 DocsLayoutView 가 그린다. */
export function DocsArticleView({
  sectionSlug,
  docSlug,
}: DocsArticleViewProps = {}) {
  const params = useParams();
  const section = sectionSlug ?? params.section;
  const slug = docSlug ?? params.slug;

  const { data: tree, isPending: treePending } = useDocsTree();
  const { data: doc, isPending, isError, error } = useDocArticle(section, slug);

  const articleRef = useRef<HTMLDivElement>(null);
  const { headings, activeId, select } = useTocHeadings(articleRef, [doc?.id]);

  const redirect = useMemo(
    () => resolveRedirect(tree, section, slug),
    [tree, section, slug],
  );

  // `/docs/faq/index` 로 직접 들어온 경우. 접힌 주소가 정식이므로 그쪽으로 보낸다 —
  // 두 주소가 같은 본문을 서빙하면 canonical 이 갈라진다.
  if (params.slug === SECTION_INDEX_SLUG && params.section) {
    return <Navigate to={docPath(params.section, SECTION_INDEX_SLUG)} replace />;
  }

  if (isError) {
    // 서버가 죽었거나 네트워크가 끊긴 것을 "문서가 없다"고 말하면 안 된다.
    // 사용자는 있지도 않은 오타를 찾게 되고, 실제 장애는 아무도 모른다.
    if (statusOf(error) !== HTTP_STATUS.NOT_FOUND) return <ArticleLoadFailed />;

    // 없는 문서면 **404 를 내기 전에** 옛 경로 표를 먼저 본다.
    // 배포된 데스크톱 앱이 옛 slug 로 들어오기 때문이다.
    if (treePending) return <ArticleSkeleton />;
    if (redirect) return <Navigate to={redirect} replace />;
    return <NotFoundArticle />;
  }

  if (isPending || !doc) {
    return (
      <div className="px-4 py-8 sm:px-8">
        <ArticleSkeleton />
      </div>
    );
  }

  const seo = buildSeo({
    title: doc.title,
    description: doc.summary,
    path: doc.path,
    keywords: doc.keywords,
    // 초안이 검색에 잡히면 대표님이 쓰다 만 글이 노출된다.
    robots: doc.isPublished ? 'index,follow' : 'noindex,nofollow',
    og: { type: 'article' },
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'TechArticle',
      headline: doc.title,
      description: doc.summary,
      dateModified: doc.updatedAt,
      datePublished: doc.publishedAt ?? doc.updatedAt,
      articleSection: doc.sectionTitle,
      inLanguage: 'ko-KR',
    },
  });

  return (
    <>
      <SEO {...seo} />

      <div className="grid min-w-0 grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_14.5rem]">
        <article className="min-w-0 px-4 py-8 sm:px-8 xl:pr-2">
          <nav aria-label="위치" className="mb-3.5 text-[12.5px] text-neutral-400">
            <Link to={PATHS.DOCS} className="hover:text-neutral-600">
              도움말
            </Link>
            <span className="px-1.5 text-neutral-300">›</span>
            <span className="text-neutral-500">{doc.sectionTitle}</span>
          </nav>

          <h1 className="text-2xl leading-tight font-extrabold tracking-tight text-neutral-900 sm:text-[31px] dark:text-neutral-100">
            {doc.title}
          </h1>
          <p className="mt-3 text-[15.5px] leading-[1.75] text-neutral-600 dark:text-neutral-400">
            {doc.summary}
          </p>

          <div className="mt-4.5 flex flex-wrap items-center gap-2 border-b border-neutral-200 pb-5 text-xs text-neutral-400 dark:border-white/12">
            <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-[11.5px] font-bold text-neutral-600 dark:bg-white/8 dark:text-neutral-300">
              {doc.sectionTitle}
            </span>
            {doc.minAppVersion && (
              <span className="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-[11.5px] font-bold text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/12 dark:text-brand-300">
                {doc.minAppVersion} 이상
              </span>
            )}
            {!doc.isPublished && (
              <span className="rounded-full bg-warning/15 px-2.5 py-1 text-[11.5px] font-bold text-amber-700 dark:text-amber-300">
                초안 — 나에게만 보입니다
              </span>
            )}
            <span className="text-neutral-300">·</span>
            최종 수정 {doc.updatedAt.slice(0, ISO_DATE_LENGTH)}
          </div>

          <div ref={articleRef} className="mt-2">
            <DocsBodyRenderer body={doc.body} />
          </div>

          <DocsPager prev={doc.prev} next={doc.next} />
          <DocsFeedback key={doc.id} docId={doc.id} title={doc.title} />
        </article>

        <aside className="hidden border-l border-neutral-200 px-5 py-8 xl:block dark:border-white/12">
          <div className="sticky top-20">
            <DocsToc headings={headings} activeId={activeId} onSelect={select} />
          </div>
        </aside>
      </div>
    </>
  );
}

/** axios 에러에서 HTTP status 만 꺼낸다. 네트워크 오류면 undefined. */
function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status;
}

/** 문서는 있을 수도 있는데 못 가져온 경우. 새로고침이 답이라고 말해준다. */
function ArticleLoadFailed() {
  return (
    <div className="px-4 py-16 text-center sm:px-8">
      <p className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
        문서를 불러오지 못했습니다
      </p>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        일시적인 문제일 수 있습니다. 잠시 뒤 새로고침해 주세요.
      </p>
    </div>
  );
}

/**
 * 없는 문서. 도움말 안에서 처리한다 — 전역 404 로 보내면 사이드바가 사라져서
 * 사용자가 "뭘 찾다 실패했는지" 맥락을 통째로 잃는다.
 */
function NotFoundArticle() {
  return (
    <div className="px-4 py-16 text-center sm:px-8">
      <SEO
        {...buildSeo({
          title: '문서를 찾을 수 없습니다',
          path: PATHS.DOCS,
          robots: 'noindex,follow',
        })}
      />
      <p className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
        문서를 찾을 수 없습니다
      </p>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        주소가 바뀌었거나 삭제된 문서입니다. 왼쪽 목록에서 다시 찾아보세요.
      </p>
      <Link
        to={PATHS.DOCS}
        className="mt-5 inline-block rounded-lg border border-neutral-200 px-4 py-2 text-sm font-semibold text-brand-600 dark:border-white/12 dark:text-brand-400"
      >
        도움말 홈으로 →
      </Link>
    </div>
  );
}
